import "flag-icons/css/flag-icons.min.css";

import { t } from "@lingui/macro";
import {
  Briefcase,
  Building,
  CaretDown,
  Check,
  CircleNotch,
  Crown,
  FileText,
  Globe,
  MagnifyingGlass,
  Sparkle,
  User,
  X,
} from "@phosphor-icons/react";
import { Dialog, DialogContent, Input, Textarea } from "@stylingcv/ui";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { cn } from "@stylingcv/utils";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { RESUMES_KEY } from "@/client/constants/query-keys";
import { useTokenModal } from "@/client/contexts/token-modal-context";
import { toast } from "@/client/hooks/use-toast";
import { useCentralizedTokens } from "@/client/hooks/use-centralized-tokens";
import { queryClient } from "@/client/libs/query-client";
import { agentsService, type GenerateCoverLetterParams } from "@/client/services/agents.service";
import { createCoverLetterDocument } from "@/client/services/resume/resume";
import { useAuthStore } from "@/client/stores/auth";
import { useUserAccess } from "@/client/hooks/use-user-access";
import { useResumeStore } from "@/client/stores/resume";
import { trackAIEvent } from "@/client/utils/gtm";
import { RESUME_LIMITS } from "@stylingcv/config";

type CoverLetterResult = {
  content: string;
  jobTitle: string;
  company: string;
  documentId?: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onGenerated: (result: CoverLetterResult) => void;
  sourceResume?: {
    id: string;
    data: any;
  };
};

type Tone = "professional" | "enthusiastic" | "friendly" | "formal";

const TONE_EMOJIS: Record<Tone, string> = {
  professional: "💼",
  enthusiastic: "🚀",
  friendly: "😊",
  formal: "🎩",
};

const COVER_LETTER_LANGUAGES = [
  { locale: "en-US", name: "English",    native: "English",    flag: "gb" },
  { locale: "ar-SA", name: "Arabic",     native: "العربية",    flag: "sa" },
  { locale: "fr-FR", name: "French",     native: "Français",   flag: "fr" },
  { locale: "de-DE", name: "German",     native: "Deutsch",    flag: "de" },
  { locale: "es-ES", name: "Spanish",    native: "Español",    flag: "es" },
  { locale: "it-IT", name: "Italian",    native: "Italiano",   flag: "it" },
  { locale: "pt-BR", name: "Portuguese", native: "Português",  flag: "br" },
  { locale: "nl-NL", name: "Dutch",      native: "Nederlands", flag: "nl" },
  { locale: "ru-RU", name: "Russian",    native: "Русский",    flag: "ru" },
  { locale: "zh-CN", name: "Chinese",    native: "中文",        flag: "cn" },
  { locale: "ja-JP", name: "Japanese",   native: "日本語",      flag: "jp" },
  { locale: "ko-KR", name: "Korean",     native: "한국어",      flag: "kr" },
  { locale: "hi-IN", name: "Hindi",      native: "हिन्दी",     flag: "in" },
  { locale: "tr-TR", name: "Turkish",    native: "Türkçe",     flag: "tr" },
  { locale: "pl-PL", name: "Polish",     native: "Polski",     flag: "pl" },
  { locale: "sv-SE", name: "Swedish",    native: "Svenska",    flag: "se" },
  { locale: "da-DK", name: "Danish",     native: "Dansk",      flag: "dk" },
  { locale: "fi-FI", name: "Finnish",    native: "Suomi",      flag: "fi" },
  { locale: "nb-NO", name: "Norwegian",  native: "Norsk",      flag: "no" },
];

const COVER_LETTER_COST = 300;

export const CoverLetterGenerationModal = ({ isOpen, onClose, onGenerated, sourceResume }: Props) => {
  const navigate = useNavigate();
  const [jobTitle, setJobTitle] = useState("");
  const [company, setCompany] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [tone, setTone] = useState<Tone>("professional");
  const [isGenerating, setIsGenerating] = useState(false);

  const storeResumeId = useResumeStore((state) => state.resume.id);
  const storeResumeData = useResumeStore((state) => state.resume.data);
  const storeLocale = useResumeStore((state) => (state.resume.data as any).metadata?.locale || "en-US");
  const resumeId = sourceResume?.id ?? storeResumeId;
  const resumeData = sourceResume?.data ?? storeResumeData;
  const basics = resumeData?.basics;
  const experience = resumeData?.sections?.experience;
  const locale = (resumeData as any)?.metadata?.locale || storeLocale || "en-US";
  const [selectedLocale, setSelectedLocale] = useState<string>(locale);
  const [langSearch, setLangSearch] = useState("");
  const [langPickerOpen, setLangPickerOpen] = useState(false);

  const { tokenBalance } = useCentralizedTokens();
  const { showTokenModal } = useTokenModal();
  const subscriptionTier = useAuthStore((state) => state.subscriptionTier);
  const { isProfessional, isEnterprise } = useUserAccess();
  const canUseCoverLetterAI = isProfessional || isEnterprise;

  const selectedLang = COVER_LETTER_LANGUAGES.find((l) => l.locale === selectedLocale);

  const filteredLangs = COVER_LETTER_LANGUAGES.filter((l) => {
    const q = langSearch.trim().toLowerCase();
    return !q || l.name.toLowerCase().includes(q) || l.native.toLowerCase().includes(q);
  });

  const getSuggestedJobTitle = (): string => {
    if (basics?.headline) return basics.headline;
    if (experience?.items && experience.items.length > 0) {
      const mostRecent = experience.items[0];
      if (mostRecent?.position) return mostRecent.position;
    }
    return "";
  };

  useEffect(() => {
    if (isOpen) {
      const suggested = getSuggestedJobTitle();
      if (suggested && !jobTitle) setJobTitle(suggested);
      // Sync language to current resume locale when modal opens
      setSelectedLocale(locale);
    }
  }, [isOpen, locale]);

  const handleClose = () => {
    if (isGenerating) return;
    setLangPickerOpen(false);
    setLangSearch("");
    onClose();
  };

  const handleGenerate = async () => {
    if (!jobTitle.trim() || !company.trim()) {
      toast({
        title: t`Missing Information`,
        description: t`Please provide at least the job title and company name.`,
        variant: "error",
      });
      return;
    }

    // Pre-flight token check — show modal instead of letting server reject
    if (tokenBalance !== null && tokenBalance < COVER_LETTER_COST) {
      showTokenModal({
        requiredTokens: COVER_LETTER_COST,
        currentTokens: tokenBalance,
        featureName: t`Cover Letter Generator`,
      });
      return;
    }

    // Pre-flight document limit check — prevent spending tokens on a creation that will fail
    const cachedResumes = queryClient.getQueryData<any[]>(RESUMES_KEY) ?? [];
    const tier = (subscriptionTier ?? "FREE") as keyof typeof RESUME_LIMITS;
    const maxDocs = RESUME_LIMITS[tier] ?? RESUME_LIMITS.FREE;
    if (cachedResumes.length >= maxDocs) {
      toast({
        title: t`Document Limit Reached`,
        description: t`You have reached the maximum number of documents for your plan. Please upgrade to create more.`,
        variant: "error",
      });
      navigate("/pricing");
      return;
    }

    setIsGenerating(true);

    try {
      const resumeContent = JSON.stringify(resumeData, null, 2);
      const language = selectedLang?.name || "English";

      const params: GenerateCoverLetterParams = {
        jobTitle, company,
        jobDescription: jobDescription || `Position: ${jobTitle} at ${company}`,
        resumeContent, tone, language,
      };

      const result = await agentsService.generateCoverLetter(params);
      trackAIEvent.coverLetterGenerated();

      const coverLetterDoc = await createCoverLetterDocument({
        sourceResumeId: resumeId,
        body: result.content,
        jobTitle,
        companyName: company,
        tone,
      });

      queryClient.invalidateQueries({ queryKey: RESUMES_KEY });

      toast({
        title: t`Cover Letter Created`,
        description: t`Your cover letter has been saved as a separate document.`,
      });

      onGenerated({ content: result.content, jobTitle, company, documentId: coverLetterDoc.id });
      onClose();
      navigate(`/builder/${coverLetterDoc.id}`);

      setJobTitle("");
      setCompany("");
      setJobDescription("");
    } catch (error: any) {
      const message: string = error?.response?.data?.message || "";
      if (message.toLowerCase().includes("insufficient") || message.toLowerCase().includes("token")) {
        showTokenModal({
          requiredTokens: COVER_LETTER_COST,
          currentTokens: tokenBalance ?? 0,
          featureName: t`Cover Letter Generator`,
          onRetry: () => handleGenerate(),
        });
      } else {
        toast({
          title: t`Generation Failed`,
          description: message || t`Failed to generate cover letter. Please try again.`,
          variant: "error",
        });
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const tones: { id: Tone; label: string; description: string }[] = [
    { id: "professional", label: t`Professional`, description: t`Formal and business-appropriate` },
    { id: "enthusiastic", label: t`Enthusiastic`, description: t`Energetic and passionate` },
    { id: "friendly", label: t`Friendly`, description: t`Warm and approachable` },
    { id: "formal", label: t`Formal`, description: t`Traditional and conservative` },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="flex w-[calc(100vw-2rem)] max-w-xl flex-col gap-0 overflow-hidden p-0 [&>button:last-child]:hidden">
        <VisuallyHidden>
          <span>{t`AI Cover Letter Generator`}</span>
        </VisuallyHidden>

        {/* Header */}
        <div className="flex shrink-0 items-center gap-3.5 border-b border-gray-100 bg-gradient-to-b from-white to-[#FAFAFA] px-5 pb-3.5 pt-4">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#155469] to-[#E8885E]">
            <Sparkle size={16} weight="fill" className="text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-[20px] font-bold leading-tight tracking-[-0.01em] text-[#155469] [font-family:var(--font-serif,Georgia,serif)]">
              {t`AI Cover Letter Generator`}
            </h2>
            <p className="mt-0.5 text-[13px] text-gray-500">
              {t`Tailored to your resume and target job`}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={isGenerating}
            className="flex size-8 shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40"
          >
            <X size={16} />
            <span className="sr-only">{t`Close`}</span>
          </button>
        </div>

        {/* Upgrade wall for non-Professional users */}
        {!canUseCoverLetterAI && (
          <div className="flex flex-col items-center gap-5 px-8 py-10 text-center">
            <div className="flex size-16 items-center justify-center rounded-2xl bg-gradient-to-br from-[#155469] to-[#E8885E] shadow-lg">
              <Crown size={28} weight="fill" className="text-white" />
            </div>
            <div>
              <h3 className="text-[18px] font-bold text-gray-900">
                {t`Professional Feature`}
              </h3>
              <p className="mt-1.5 text-[14px] leading-relaxed text-gray-500">
                {t`AI Cover Letter generation is available on Professional and Enterprise plans. Upgrade to create tailored cover letters in seconds.`}
              </p>
            </div>

            {/* Feature highlights */}
            <div className="w-full space-y-2 rounded-xl border border-[rgba(38,133,153,0.15)] bg-[rgba(38,133,153,0.04)] p-4 text-left">
              {[
                t`Generate cover letters tailored to any job`,
                t`Choose tone: professional, friendly, formal`,
                t`Write in 19 languages`,
                t`Unlimited AI token allocation`,
              ].map((feature) => (
                <div key={feature} className="flex items-center gap-2.5">
                  <Check size={14} weight="bold" className="shrink-0 text-[#268599]" />
                  <span className="text-[13px] text-gray-700">{feature}</span>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={() => { handleClose(); navigate("/pricing"); }}
              className="flex w-full items-center justify-center gap-2 rounded-[9px] bg-gradient-to-br from-[#155469] via-[#268599] to-[#E8885E] py-[11px] text-[13px] font-bold text-white shadow-[0_4px_14px_rgba(38,133,153,0.30)] transition-opacity hover:opacity-90"
            >
              <Crown size={14} weight="fill" className="shrink-0" />
              {t`Upgrade to Professional`}
            </button>
          </div>
        )}

        {/* Scrollable body — only shown to Professional+ */}
        {canUseCoverLetterAI && <div className="max-h-[calc(90vh-140px)] overflow-y-auto">
          <div className="space-y-5 px-[22px] py-5">

            {/* User info banner */}
            {basics?.name && (
              <div className="flex items-start gap-3 rounded-xl border border-[#268599]/15 bg-[rgba(38,133,153,0.05)] px-4 py-3">
                <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[rgba(38,133,153,0.12)]">
                  <User size={15} className="text-[#268599]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[12px] font-semibold uppercase tracking-wide text-[#268599]/70">
                    {t`Creating cover letter for`}
                  </p>
                  <p className="mt-0.5 truncate text-[13px] font-semibold text-gray-800">{basics.name}</p>
                  {basics.email && (
                    <p className="truncate text-[12px] text-gray-400">{basics.email}</p>
                  )}
                  {basics.headline && (
                    <p className="mt-0.5 truncate text-[12px] text-[#268599]/80">{basics.headline}</p>
                  )}
                </div>
              </div>
            )}

            {/* Job Title */}
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-[13px] font-semibold text-gray-700">
                <Briefcase size={13} className="text-gray-400" />
                {t`Job Title`}
                <span className="text-[#E8885E]">*</span>
              </label>
              <Input
                value={jobTitle}
                placeholder={t`e.g., Senior Software Engineer`}
                onChange={(e) => setJobTitle(e.target.value)}
                className="h-10 border-gray-200 bg-white text-[13px] text-gray-800 placeholder:text-gray-400 focus-visible:border-[#268599] focus-visible:ring-[#268599]/20"
              />
            </div>

            {/* Company */}
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-[13px] font-semibold text-gray-700">
                <Building size={13} className="text-gray-400" />
                {t`Company Name`}
                <span className="text-[#E8885E]">*</span>
              </label>
              <Input
                value={company}
                placeholder={t`e.g., Google, Microsoft, Startup Inc.`}
                onChange={(e) => setCompany(e.target.value)}
                className="h-10 border-gray-200 bg-white text-[13px] text-gray-800 placeholder:text-gray-400 focus-visible:border-[#268599] focus-visible:ring-[#268599]/20"
              />
            </div>

            {/* Job Description */}
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-[13px] font-semibold text-gray-700">
                <FileText size={13} className="text-gray-400" />
                {t`Job Description`}
                <span className="text-[12px] font-normal text-gray-400">({t`Optional but recommended`})</span>
              </label>
              <Textarea
                value={jobDescription}
                placeholder={t`Paste the job description here for a more tailored cover letter...`}
                rows={4}
                onChange={(e) => setJobDescription(e.target.value)}
                className="resize-none rounded-lg border-gray-200 text-[13px] text-gray-800 placeholder:text-gray-400 focus-visible:border-[#268599] focus-visible:ring-[#268599]/20 focus-visible:outline-none"
              />
            </div>

            {/* Tone */}
            <div className="space-y-2">
              <label className="text-[13px] font-semibold text-gray-700">{t`Tone`}</label>
              <div className="grid grid-cols-2 gap-2">
                {tones.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setTone(item.id)}
                    className={cn(
                      "rounded-xl border px-3 py-2.5 text-left transition-all rtl:text-right",
                      tone === item.id
                        ? "border-[#268599]/30 bg-[rgba(38,133,153,0.06)]"
                        : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50",
                    )}
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="text-[14px]">{TONE_EMOJIS[item.id]}</span>
                      <span className={cn(
                        "text-[13px] font-semibold",
                        tone === item.id ? "text-[#268599]" : "text-gray-800",
                      )}>
                        {item.label}
                      </span>
                    </div>
                    <p className="mt-0.5 text-[11px] text-gray-400">{item.description}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Language */}
            <div className="space-y-2">
              <label className="flex items-center gap-1.5 text-[13px] font-semibold text-gray-700">
                <Globe size={13} className="text-gray-400" />
                {t`Cover Letter Language`}
              </label>

              {/* Trigger row */}
              <button
                type="button"
                onClick={() => { setLangPickerOpen((v) => !v); setLangSearch(""); }}
                className="flex h-10 w-full items-center gap-2.5 rounded-lg border border-gray-200 bg-white px-3 text-left transition-colors hover:border-gray-300"
              >
                {selectedLang && (
                  <span className={`fi fi-${selectedLang.flag} shrink-0 text-[18px]`} />
                )}
                <span className="flex-1 text-[13px] text-gray-800">
                  {selectedLang ? `${selectedLang.name} — ${selectedLang.native}` : t`Select language`}
                </span>
                <CaretDown
                  size={13}
                  className={cn("shrink-0 text-gray-400 transition-transform", langPickerOpen && "rotate-180")}
                />
              </button>

              {/* Inline picker */}
              {langPickerOpen && (
                <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-md">
                  <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2">
                    <MagnifyingGlass size={13} className="shrink-0 text-gray-400" />
                    <input
                      autoFocus
                      value={langSearch}
                      onChange={(e) => setLangSearch(e.target.value)}
                      placeholder={t`Search languages...`}
                      className="flex-1 bg-transparent text-[13px] outline-none placeholder:text-gray-400"
                    />
                  </div>
                  <div className="max-h-[180px] overflow-y-auto py-1">
                    {filteredLangs.map((lang) => (
                      <button
                        key={lang.locale}
                        type="button"
                        onClick={() => {
                          setSelectedLocale(lang.locale);
                          setLangPickerOpen(false);
                          setLangSearch("");
                        }}
                        className={cn(
                          "flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors hover:bg-gray-50",
                          selectedLocale === lang.locale && "bg-[rgba(38,133,153,0.06)]",
                        )}
                      >
                        <span className={`fi fi-${lang.flag} shrink-0 text-[16px]`} />
                        <span className="text-[13px] text-gray-800">{lang.name}</span>
                        <span className="text-[12px] text-gray-400">{lang.native}</span>
                        {selectedLocale === lang.locale && (
                          <Check size={13} className="ml-auto shrink-0 text-[#268599]" />
                        )}
                      </button>
                    ))}
                    {filteredLangs.length === 0 && (
                      <p className="px-3 py-4 text-center text-[13px] text-gray-400">{t`No languages found`}</p>
                    )}
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>}

        {/* Footer — only shown to Professional+ */}
        {canUseCoverLetterAI &&
        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-gray-100 bg-[#FAFAFA] px-[22px] py-[14px]">
          <div className="flex items-center gap-1.5 text-[12px] font-semibold text-gray-400">
            <Sparkle size={12} className="text-amber-400" weight="fill" />
            {t`Costs 300 tokens`}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={isGenerating}
              className="cursor-pointer rounded-[9px] border border-gray-300 bg-white px-[18px] py-[9px] text-[13px] font-semibold text-gray-900 transition-colors hover:border-gray-400 disabled:opacity-40"
            >
              {t`Cancel`}
            </button>
            <button
              type="button"
              disabled={isGenerating || !jobTitle.trim() || !company.trim()}
              onClick={handleGenerate}
              className="flex cursor-pointer items-center gap-1.5 rounded-[9px] bg-gradient-to-br from-[#155469] via-[#268599] to-[#E8885E] px-[18px] py-[9px] text-[13px] font-bold text-white shadow-[0_4px_14px_rgba(38,133,153,0.30)] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {isGenerating ? (
                <>
                  <CircleNotch size={14} className="animate-spin" />
                  {t`Generating...`}
                </>
              ) : (
                <>
                  <Sparkle size={14} weight="fill" />
                  {t`Generate Cover Letter`}
                </>
              )}
            </button>
          </div>
        </div>}
      </DialogContent>
    </Dialog>
  );
};
